/**
 * Evaluate widget-scope rules for one URL.
 *
 * Ordered list, FIRST MATCH WINS — resolved PER SURFACE, not per rule. For
 * each surface, walk top to bottom; the first enabled rule that matches the
 * host and path AND names that surface decides it, and the walk stops there.
 *
 * Per-surface rather than per-rule because the alternative is unusable: a
 * whole-rule walk would let a rule that matched the URL but only mentioned
 * `voice` consume the decision for every other surface, so authors would have
 * to enumerate the full surface set on every rule and a later rule could
 * silently switch back on what an earlier one turned off. Per-surface lets
 * "deny voice everywhere" and "deny the bar on /blog/**" coexist without
 * either one knowing about the other.
 *
 * Pure, total, synchronous, and it must never throw — a bug in here would take
 * out every surface on every page, so callers wrap it defensively and it
 * returns a complete answer for every known surface or none at all.
 */
import { matchHost, matchPath } from "./glob";
// ZOD-FREE IMPORTS ONLY at runtime. This module is bundled into the host-page
// snippet, whose build fails if zod reaches it — so the surface list comes from
// ./surfaces (plain data, compile-checked against the Pkl vocabulary) and
// everything from ./schema is `import type`, which erases.
import { ALL_SURFACES, WIDGET_SURFACES, type SurfaceGates, type WidgetSurface } from "./surfaces";
import type { WidgetScope, WidgetScopeRule } from "./schema";

/** Surfaces this bundle knows. */
const KNOWN: readonly WidgetSurface[] = WIDGET_SURFACES;

export type WidgetScopeInput = {
  /** window.location.hostname — no port, no scheme. */
  host: string;
  /** Full URL or bare pathname; only the path is consulted. */
  url: string;
};

export type WidgetScopeResult = {
  /** Total map: every known surface present, never partial. */
  surfaces: SurfaceGates;
  /**
   * Which rule decided each surface; null where nothing matched and the
   * default (on) applied. Powers the dashboard's "test a URL" preview and
   * widget debug logging — an ordered first-match list is otherwise very hard
   * to reason about from the outside.
   */
  decidedBy: { readonly [K in WidgetSurface]: WidgetScopeRule | null };
};

// The two casts below are NOT trust-boundary casts: each builds its own value by
// looping KNOWN (the authoritative surface list) and no external data is
// involved, so there is nothing to validate — only TS's inability to prove a
// loop filled a mapped type. Left as-is deliberately; see CLAUDE.md, which bans
// a cast standing in for a CHECK, not one standing in for a proof.
/** Everything on — the answer whenever rules are absent, broken, or silent. */
export function allSurfacesOn(): SurfaceGates {
  const out: Record<string, boolean> = {};
  for (const s of KNOWN) out[s] = true;
  return out as SurfaceGates;
}

/** Everything off — master-off, and a rule denying `*`. */
export function allSurfacesOff(): SurfaceGates {
  const out: Record<string, boolean> = {};
  for (const s of KNOWN) out[s] = false;
  return out as SurfaceGates;
}

/**
 * Does `rule` speak about `surface`?
 *
 * `*` covers everything. Otherwise a name must match exactly — and note what
 * happens to names this bundle does not recognise: they simply never equal a
 * known surface, so they decide nothing. That is the correct outcome for an
 * ALLOW rule (nothing is granted that was not asked for). For a DENY rule it
 * is handled separately, in the walk below.
 */
function ruleNames(rule: WidgetScopeRule, surface: WidgetSurface): boolean {
  return rule.surfaces.includes(ALL_SURFACES) || rule.surfaces.includes(surface);
}

/**
 * A deny rule listing ONLY names this bundle does not know.
 *
 * The intent is unambiguous — the author is switching something off — but the
 * target is unintelligible to this bundle. Failing open would show a surface
 * a tenant asked to hide; failing closed on a name we cannot resolve would
 * hide surfaces nobody named. Neither is defensible in general, so we treat
 * such a rule as denying the surfaces it *does* resolve, which is none — i.e.
 * it decides nothing and the walk continues to later rules. The one thing we
 * never do is discard the ruleset over it.
 */
function isUnresolvableDeny(rule: WidgetScopeRule): boolean {
  if (rule.action !== "deny") return false;
  if (rule.surfaces.includes(ALL_SURFACES)) return false;
  return !rule.surfaces.some((s) => (KNOWN as readonly string[]).includes(s));
}

export function evaluateWidgetScope(
  scope: WidgetScope,
  input: WidgetScopeInput,
): WidgetScopeResult {
  const surfaces: Record<string, boolean> = {};
  const decidedBy: Record<string, WidgetScopeRule | null> = {};

  // Host and path are the same for every surface — match each rule once.
  const matched: WidgetScopeRule[] = [];
  for (const rule of scope.rules) {
    if (!rule.enabled) continue;
    if (isUnresolvableDeny(rule)) continue;
    if (!matchHost(input.host, rule.domain)) continue;
    if (!matchPath(rule.path, input.url)) continue;
    matched.push(rule);
  }

  for (const surface of KNOWN) {
    const decider = matched.find((r) => ruleNames(r, surface)) ?? null;
    surfaces[surface] = decider === null ? true : decider.action === "allow";
    decidedBy[surface] = decider;
  }

  return {
    surfaces: surfaces as SurfaceGates,
    decidedBy: decidedBy as WidgetScopeResult["decidedBy"],
  };
}

/**
 * Evaluate, but never fail. Any throw — a pathological pattern, a shape that
 * slipped past parsing — yields every surface on rather than a dead widget.
 */
export function evaluateWidgetScopeSafe(
  scope: WidgetScope,
  input: WidgetScopeInput,
): SurfaceGates {
  try {
    return evaluateWidgetScope(scope, input).surfaces;
  } catch {
    return allSurfacesOn();
  }
}
